import type { Ref } from 'vue'
import type { ContextState } from '../models'
import type { IToolingBaseComposableDataItem } from './use-tooling-base'
import type { IToolingFormComposable } from './use-tooling-form'

import { computed, getCurrentInstance, watch } from 'vue'

import { FilterIdEnum } from '../enums'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { useBrowseListFilter } from './use-browse-list-filter'
import { mapGetters } from './utils'

interface PrefillBrowseContextParams<
  T extends IToolingBaseComposableDataItem = IToolingBaseComposableDataItem,
> {
  contextAttr?: string
  contextFacet?: string
  toolingForm: IToolingFormComposable<T>
}

interface BrowseCriteria {
  browseField?: string
  browseValue?: string
}

interface FacetCriteria {
  context?: {
    name: string
    values: string[]
  }[]
}

interface PrefillPivotsParams<T extends IToolingBaseComposableDataItem> {
  toolingForm: IToolingFormComposable<T>
  selectedFiltersValues: Record<string, any>
  skipFilter?: (filterId: string) => boolean
}

interface PrefillEndpointParams<T extends IToolingBaseComposableDataItem> {
  toolingForm: IToolingFormComposable<T>
  contextEndpointId?: string
  endpoints: Array<{ value: string }>
  endpointId: Ref<string | undefined>
}

interface UseContextPrefillWatchParams<T extends IToolingBaseComposableDataItem> {
  mode: 'facet' | 'browse'
  isCreateMode: Ref<boolean>

  toolingForm: IToolingFormComposable<T>

  selectedFiltersValues: Ref<Record<string, any>>
  contextAttributeOption: Ref<string | undefined>
  contextFacetSelected: Ref<string | undefined>

  contextEndpointId: Ref<string | undefined>
  endpoints: Ref<Array<{ value: string }>>
  endpointId: Ref<string | undefined>

  contextBrowseField?: Ref<string | undefined>
}

function resolvePrefillValue(
  browseValue: string | undefined,
  filterId: FilterIdEnum,
  filters: Record<string, any> | undefined,
): string | undefined {
  const selectedValues = filters?.[filterId]?.selectedValues

  return browseValue ?? (Array.isArray(selectedValues) ? selectedValues[0] : undefined)
}

const prefillPivots = <T extends IToolingBaseComposableDataItem>({
  toolingForm,
  selectedFiltersValues,
  skipFilter,
}: PrefillPivotsParams<T>): void => {
  const pivots = (toolingForm.item.value.criteria.pivots ||= [])

  Object.entries(selectedFiltersValues ?? {}).forEach(([filterId, filter]) => {
    if (skipFilter?.(filterId)) {
      return
    }

    const values = filter.selectedValues as string[]

    if (!toolingForm.pivotsConfig.value[filterId] || !values?.length) {
      return
    }

    const pivot = { name: filterId, values: [...values] }
    const pivotIndex = pivots.findIndex(({ name }) => name === filterId)

    if (pivotIndex < 0) {
      pivots.push(pivot)
    } else {
      pivots.splice(pivotIndex, 1, pivot)
    }
  })
}

export function useContextPrefillSource() {
  const ctx = getCurrentInstance()

  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const { browseFilterValue } = useBrowseListFilter()

  const selectedFiltersValues = computed(() => context.value.filters)

  const endpointId = computed(() => context.value.endpointId ?? 'search')

  const contextBrowseField = computed(() =>
    resolvePrefillValue(
      browseFilterValue.value.name,
      FilterIdEnum.ATTRIBUTES,
      selectedFiltersValues.value,
    ),
  )

  const contextBrowseValue = computed(() =>
    resolvePrefillValue(
      browseFilterValue.value.value,
      FilterIdEnum.ATTRIBUTES_VALUE,
      selectedFiltersValues.value,
    ),
  )

  return {
    selectedFiltersValues,
    endpointId,
    contextBrowseField,
    contextBrowseValue,
  }
}

export function useContextPrefill() {
  const prefillEndpoint = <T extends IToolingBaseComposableDataItem>({
    toolingForm,
    contextEndpointId,
    endpoints,
    endpointId,
  }: PrefillEndpointParams<T>): void => {
    if (contextEndpointId && endpoints.length > 1) {
      endpointId.value = contextEndpointId
      ;(toolingForm.item.value as any).endpointId = contextEndpointId
    }
  }

  const prefillBrowseCriteria = <T extends IToolingBaseComposableDataItem>({
    contextAttr,
    contextFacet,
    toolingForm,
  }: PrefillBrowseContextParams<T>) => {
    if (!contextAttr) {
      return
    }

    Object.assign(toolingForm.item.value.criteria as BrowseCriteria, {
      browseField: contextAttr,
      ...(contextFacet && { browseValue: contextFacet }),
    })
  }

  const prefillFacetCriteria = <T extends IToolingBaseComposableDataItem>({
    contextAttr,
    contextFacet,
    toolingForm,
  }: PrefillBrowseContextParams<T>) => {
    if (!contextAttr) {
      return
    }

    const criteria = toolingForm.item.value.criteria as FacetCriteria

    criteria.context = [
      { name: 'browseField', values: [contextAttr] },
      ...(contextFacet ? [{ name: 'browseValue', values: [contextFacet] }] : []),
    ]
  }

  const setupContextPrefillWatch = <T extends IToolingBaseComposableDataItem>({
    mode,
    isCreateMode,
    toolingForm,
    selectedFiltersValues,
    contextAttributeOption,
    contextFacetSelected,
    contextEndpointId,
    endpoints,
    endpointId,
    contextBrowseField,
  }: UseContextPrefillWatchParams<T>) => {
    const prefillBrowseContext: (params: PrefillBrowseContextParams<T>) => void =
      mode === 'facet' ? prefillFacetCriteria : prefillBrowseCriteria
    watch(
      (): [T, string | undefined, string | undefined] => [
        toolingForm.item.value,
        contextAttributeOption.value,
        contextFacetSelected.value,
      ],
      ([, contextAttr, contextFacet]) => {
        if (!isCreateMode.value) {
          return
        }

        prefillPivots({
          toolingForm,
          selectedFiltersValues: selectedFiltersValues.value,
          skipFilter:
            mode === 'browse' ? filterId => filterId === FilterIdEnum.ENDPOINT : undefined,
        })

        prefillEndpoint({
          toolingForm,
          contextEndpointId: contextEndpointId.value,
          endpoints: endpoints.value,
          endpointId,
        })

        prefillBrowseContext({ contextAttr, contextFacet, toolingForm })

        if (mode === 'facet' && contextAttr && contextBrowseField) {
          contextBrowseField.value = contextAttr
        }
      },
      { immediate: true },
    )
  }

  return {
    prefillPivots,
    prefillEndpoint,
    prefillBrowseCriteria,
    prefillFacetCriteria,
    setupContextPrefillWatch,
  }
}
